import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createSafeWaveSurfer } from '../utils/safeLazyLoad';
import { RecordingService } from '../utils/recordingService'; // Add this import
import { 
  Play, 
  Pause, 
  RotateCcw, 
  ZoomIn, 
  ZoomOut, 
  MessageSquare, 
  Bookmark, 
  SkipBack,
  SkipForward,
  Volume2,
  Repeat,
  Edit3,
  Save,
  X,
  Download
} from 'lucide-react';

const AudioPlayer = ({ 
  audioUrl, 
  recordingId,
  practiceItemId,
  sessionId,
  userId,
  onDataUpdate,
  initialRegions = [], 
  initialComments = [],
  initialMarkers = [],
  compact = false,
  readOnly = false
}) => {
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const regionsPluginRef = useRef(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [zoom, setZoom] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isLooping, setIsLooping] = useState(false);
  const [loopRegion, setLoopRegion] = useState(null);
  
  // Modal states
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showMarkerModal, setShowMarkerModal] = useState(false);
  const [showRegionModal, setShowRegionModal] = useState(false);
  const [commentTime, setCommentTime] = useState(0);
  const [markerTime, setMarkerTime] = useState(0);
  const [editingComment, setEditingComment] = useState(null);
  const [editingMarker, setEditingMarker] = useState(null);
  
  // Data states
  const [comments, setComments] = useState(initialComments);
  const [markers, setMarkers] = useState(initialMarkers);
  const [regions, setRegions] = useState(initialRegions);

  // Sync annotation state when props arrive asynchronously (e.g. after
  // Firestore audioData loads in ReviewView).
  useEffect(() => {
    if (initialComments.length > 0) setComments(initialComments);
  }, [initialComments]);

  useEffect(() => {
    if (initialMarkers.length > 0) setMarkers(initialMarkers);
  }, [initialMarkers]);

  useEffect(() => {
    if (initialRegions.length > 0) {
      setRegions(initialRegions);
      if (regionsPluginRef.current) {
        const existing = regionsPluginRef.current.getRegions();
        existing.forEach(r => r.remove());
        initialRegions.forEach(region => {
          regionsPluginRef.current.addRegion({
            id: region.id,
            start: region.start,
            end: region.end,
            color: region.color || 'rgba(255, 0, 0, 0.1)',
            content: region.label || '',
            drag: !readOnly,
            resize: !readOnly
          });
        });
      }
    }
  }, [initialRegions, readOnly]);

  const [audioUrlError, setAudioUrlError] = useState(false);
  const [refreshedUrl, setRefreshedUrl] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isWaveformLoading, setIsWaveformLoading] = useState(true);


  // Initialize WaveSurfer
  useEffect(() => {
    const tag = `[AudioPlayer ${recordingId?.slice(0, 8) || '?'}]`;
    let media = null;
    let onMediaLoadStart = null;
    let onMediaLoadedMetadata = null;
    let onMediaCanPlay = null;
    let onMediaError = null;
    let onMediaStalled = null;
    let onMediaWaiting = null;
    let stalledLoadTimeout = null;
    let blobReadyTimeout = null;
    let hardErrorTimeout = null;
    let attemptedBlobFallback = false;
    let attemptedWebAudioRetry = false;
    let cachedBlob = null;
    let didBecomeReady = false;

    if (!waveformRef.current || (!audioUrl && !refreshedUrl)) {
      console.log(`${tag} skipped: waveformRef=${!!waveformRef.current}, audioUrl=${!!audioUrl}, refreshedUrl=${!!refreshedUrl}`);
      return;
    }
    let cancelled = false;
    setIsWaveformLoading(true);

    console.log(`${tag} useEffect run — refreshedUrl=${!!refreshedUrl}, audioUrl=${audioUrl?.slice(0, 60)}...`);

    const initWaveSurfer = async () => {
      try {
      let urlToLoad = refreshedUrl || audioUrl;
      const legacyDetection = (() => {
        // Legacy recordings from older session buckets can hang after loadstart
        // in MediaElement mode. For those, prefer fetch+blob loading upfront.
        const currentYear = new Date().getFullYear();
        const fromSessionId = { matched: false, ts: null, year: null, shouldPrefer: false };
        const fromUrlPath = { matched: false, ts: null, year: null, shouldPrefer: false };
        if (urlToLoad && typeof urlToLoad === 'string') {
          const u = decodeURIComponent(urlToLoad);
          const m = u.match(/session_(\d{10,})/);
          if (m) {
            const ts = Number(m[1]);
            if (Number.isFinite(ts)) {
              fromUrlPath.matched = true;
              fromUrlPath.ts = ts;
              fromUrlPath.year = new Date(ts).getFullYear();
              fromUrlPath.shouldPrefer = fromUrlPath.year < currentYear;
            }
          }
        }
        if (!sessionId || typeof sessionId !== 'string') {
          return { shouldPreferBlobLoad: fromUrlPath.shouldPrefer, fromSessionId, fromUrlPath };
        }
        const match = sessionId.match(/^session_(\d{10,})$/);
        if (!match) {
          return { shouldPreferBlobLoad: fromUrlPath.shouldPrefer, fromSessionId, fromUrlPath };
        }
        const ts = Number(match[1]);
        if (!Number.isFinite(ts)) {
          return { shouldPreferBlobLoad: fromUrlPath.shouldPrefer, fromSessionId, fromUrlPath };
        }
        const sessionDate = new Date(ts);
        fromSessionId.matched = true;
        fromSessionId.ts = ts;
        fromSessionId.year = sessionDate.getFullYear();
        fromSessionId.shouldPrefer = sessionDate.getFullYear() < currentYear;
        return { shouldPreferBlobLoad: fromSessionId.shouldPrefer || fromUrlPath.shouldPrefer, fromSessionId, fromUrlPath };
      })();
      const { shouldPreferBlobLoad } = legacyDetection;
      const attemptWebAudioRetry = async (reason) => {
        if (cancelled || attemptedWebAudioRetry) return;
        attemptedWebAudioRetry = true;
        console.warn(`${tag} retrying with WebAudio backend (${reason})`);
        try {
          // Tear down the stuck MediaElement instance.
          if (wavesurferRef.current) {
            try { wavesurferRef.current.destroy(); } catch (_) { /* ignore */ }
            wavesurferRef.current = null;
            regionsPluginRef.current = null;
          }
          // Detach old media listeners — they reference the destroyed element.
          if (media) {
            if (onMediaLoadStart) media.removeEventListener('loadstart', onMediaLoadStart);
            if (onMediaLoadedMetadata) media.removeEventListener('loadedmetadata', onMediaLoadedMetadata);
            if (onMediaCanPlay) media.removeEventListener('canplay', onMediaCanPlay);
            if (onMediaError) media.removeEventListener('error', onMediaError);
            if (onMediaStalled) media.removeEventListener('stalled', onMediaStalled);
            if (onMediaWaiting) media.removeEventListener('waiting', onMediaWaiting);
            media = null;
          }
          if (!waveformRef.current || cancelled) return;
          const result = await createSafeWaveSurfer(waveformRef.current, {
            backend: 'WebAudio',
            height: compact ? 64 : 128,
            cursorWidth: 2,
            barWidth: 2,
            barRadius: 3,
            normalize: false,
            interact: true,
            hideScrollbar: false,
          });
          if (cancelled || !result) return;
          wavesurferRef.current = result.wavesurfer;
          regionsPluginRef.current = result.regionsPlugin;
          wavesurferRef.current.on('error', (error) => {
            console.error(`${tag} WaveSurfer (WebAudio) error:`, error);
            if (!cancelled) { setAudioUrlError(true); setIsWaveformLoading(false); }
          });
          wavesurferRef.current.on('ready', () => {
            if (cancelled) return;
            didBecomeReady = true;
            setIsWaveformLoading(false);
            if (hardErrorTimeout) { clearTimeout(hardErrorTimeout); hardErrorTimeout = null; }
            const audioDuration = wavesurferRef.current.getDuration();
            console.log(`${tag} ready (WebAudio) — duration=${audioDuration}s`);
            setDuration(audioDuration);
            initialRegions.forEach(region => {
              regionsPluginRef.current.addRegion({
                id: region.id,
                start: region.start,
                end: region.end,
                color: region.color || 'rgba(255, 0, 0, 0.1)',
                content: region.label || '',
                drag: !readOnly,
                resize: !readOnly,
              });
            });
          });
          wavesurferRef.current.on('play', () => setIsPlaying(true));
          wavesurferRef.current.on('pause', () => setIsPlaying(false));
          wavesurferRef.current.on('timeupdate', (time) => {
            setCurrentTime(time);
            if (isLooping && loopRegion && time >= loopRegion.end) {
              wavesurferRef.current.seekTo(loopRegion.start / duration);
            }
          });
          // Prefer the already-fetched blob (already retyped via byte sniff);
          // otherwise fetch fresh and retype here too.
          let blobForRetry = cachedBlob;
          if (!blobForRetry) {
            const response = await fetch(urlToLoad, { cache: 'no-store' });
            if (!response.ok) throw new Error(`HTTP ${response.status} ${response.statusText}`);
            blobForRetry = await response.blob();
            blobForRetry = await retypeBlob(blobForRetry);
          }
          if (cancelled || !wavesurferRef.current) return;
          console.log(`${tag} loading via WebAudio (${blobForRetry.type || 'unknown'}, ${blobForRetry.size} bytes)`);
          await wavesurferRef.current.loadBlob(blobForRetry);
        } catch (err) {
          console.error(`${tag} WebAudio retry failed:`, err);
          if (!cancelled) { setAudioUrlError(true); setIsWaveformLoading(false); }
        }
      };

      // Sniff the first bytes to determine the real container format. iPad
      // Safari records as MP4/AAC but the upload pipeline historically used
      // a `.webm` filename and sometimes a mismatched Content-Type, so the
      // server-provided blob.type can lie. Re-typing the blob to match the
      // actual bytes is required for loadBlob to play correctly.
      const retypeBlob = async (blob) => {
        try {
          const head = new Uint8Array(await blob.slice(0, 32).arrayBuffer());
          let detected = null;
          // EBML/WebM/Matroska header: 1A 45 DF A3
          if (head[0] === 0x1A && head[1] === 0x45 && head[2] === 0xDF && head[3] === 0xA3) {
            detected = 'audio/webm';
          // MP4 'ftyp' box: bytes 4-7 = 'ftyp' (66 74 79 70)
          } else if (head[4] === 0x66 && head[5] === 0x74 && head[6] === 0x79 && head[7] === 0x70) {
            detected = 'audio/mp4';
          // Ogg 'OggS'
          } else if (head[0] === 0x4F && head[1] === 0x67 && head[2] === 0x67 && head[3] === 0x53) {
            detected = 'audio/ogg';
          // WAV 'RIFF' + 'WAVE'
          } else if (head[0] === 0x52 && head[1] === 0x49 && head[2] === 0x46 && head[3] === 0x46
                  && head[8] === 0x57 && head[9] === 0x41 && head[10] === 0x56 && head[11] === 0x45) {
            detected = 'audio/wav';
          }
          if (detected && detected !== blob.type) {
            console.log(`${tag} retyping blob: ${blob.type || 'unknown'} → ${detected}`);
            return new Blob([blob], { type: detected });
          }
          return blob;
        } catch (err) {
          console.warn(`${tag} blob sniff failed:`, err);
          return blob;
        }
      };

      const attemptBlobFallback = async (reason) => {
        if (cancelled || attemptedBlobFallback || !wavesurferRef.current) return;
        attemptedBlobFallback = true;
        console.warn(`${tag} fallback to blob load (${reason})`);
        try {
          const response = await fetch(urlToLoad, { cache: 'no-store' });
          if (!response.ok) {
            throw new Error(`HTTP ${response.status} ${response.statusText}`);
          }
          let blob = await response.blob();
          if (cancelled || !wavesurferRef.current) return;
          blob = await retypeBlob(blob);
          if (cancelled || !wavesurferRef.current) return;
          cachedBlob = blob;
          console.log(`${tag} blob fallback fetched (${blob.type || 'unknown'}, ${blob.size} bytes)`);
          // If loadBlob also stalls (same WebM-without-duration root cause),
          // escalate to a full WebAudio re-init that decodes via decodeAudioData.
          if (blobReadyTimeout) clearTimeout(blobReadyTimeout);
          blobReadyTimeout = setTimeout(() => {
            if (!cancelled && !didBecomeReady) {
              attemptWebAudioRetry('blob load did not reach ready');
            }
          }, 5000);
          await wavesurferRef.current.loadBlob(blob);
        } catch (blobError) {
          console.error(`${tag} blob fallback failed:`, blobError);
          // If we've already moved on to a WebAudio retry, that path owns
          // the error state — don't flip the UI to error here just because
          // the now-destroyed wavesurfer rejected its pending loadBlob.
          if (!cancelled && !attemptedWebAudioRetry) setAudioUrlError(true);
        }
      };

      if (!refreshedUrl && urlToLoad.includes('firebasestorage.googleapis.com') && userId) {
        try {
          console.log(`${tag} refreshing URL...`);
          setIsRefreshing(true);
          const recordingService = new RecordingService();
          const freshUrl = await recordingService.refreshDownloadURL(urlToLoad);
          if (cancelled) { console.log(`${tag} cancelled during refresh`); return; }
          setRefreshedUrl(freshUrl);
          setIsRefreshing(false);
          console.log(`${tag} refresh done, waiting for re-run`);
          return;
        } catch (refreshError) {
          if (cancelled) return;
          console.error(`${tag} refresh failed:`, refreshError);
          setAudioUrlError(true);
          setIsRefreshing(false);
          return;
        }
      }

      const preferredBackend = shouldPreferBlobLoad ? 'WebAudio' : 'MediaElement';
      console.log(`${tag} creating WaveSurfer, backend=${preferredBackend}, container in DOM=${document.body.contains(waveformRef.current)}, size=${waveformRef.current.offsetWidth}x${waveformRef.current.offsetHeight}`);

      const result = await createSafeWaveSurfer(waveformRef.current, {
          backend: preferredBackend,
          height: compact ? 64 : 128,
        cursorWidth: 2,
        barWidth: 2,
        barRadius: 3,
        normalize: false,
        interact: true,
        hideScrollbar: false,
      });

      if (cancelled || !result) {
        console.log(`${tag} after createSafeWaveSurfer: cancelled=${cancelled}, result=${!!result}`);
        return;
      }

      wavesurferRef.current = result.wavesurfer;
      regionsPluginRef.current = result.regionsPlugin;

      wavesurferRef.current.on('error', (error) => {
        console.error(`${tag} WaveSurfer error event:`, error);
        if (!cancelled) { setAudioUrlError(true); setIsWaveformLoading(false); }
      });

      wavesurferRef.current.on('ready', () => {
        if (cancelled) return;
        didBecomeReady = true;
        setIsWaveformLoading(false);
        if (stalledLoadTimeout) { clearTimeout(stalledLoadTimeout); stalledLoadTimeout = null; }
        if (blobReadyTimeout) { clearTimeout(blobReadyTimeout); blobReadyTimeout = null; }
        if (hardErrorTimeout) { clearTimeout(hardErrorTimeout); hardErrorTimeout = null; }
        const audioDuration = wavesurferRef.current.getDuration();
        console.log(`${tag} ready — duration=${audioDuration}s`);
        setDuration(audioDuration);
        
        initialRegions.forEach(region => {
          regionsPluginRef.current.addRegion({
            id: region.id,
            start: region.start,
            end: region.end,
            color: region.color || 'rgba(255, 0, 0, 0.1)',
            content: region.label || '',
            drag: !readOnly,
            resize: !readOnly
          });
        });
      });

      wavesurferRef.current.on('play', () => setIsPlaying(true));
      wavesurferRef.current.on('pause', () => setIsPlaying(false));
      wavesurferRef.current.on('timeupdate', (time) => {
        setCurrentTime(time);
        
        if (isLooping && loopRegion && time >= loopRegion.end) {
          wavesurferRef.current.seekTo(loopRegion.start / duration);
        }
      });

      // Track media element events to diagnose stuck loads
      media = wavesurferRef.current.getMediaElement();
      if (media) {
        onMediaLoadStart = () => console.log(`${tag} media: loadstart`);
        onMediaLoadedMetadata = () => console.log(`${tag} media: loadedmetadata, duration=${media.duration}`);
        onMediaCanPlay = () => console.log(`${tag} media: canplay`);
        onMediaError = () => {
          console.error(`${tag} media: error`, media.error);
          attemptBlobFallback('media error');
        };
        onMediaStalled = () => {
          console.warn(`${tag} media: stalled`);
          attemptBlobFallback('media stalled');
        };
        onMediaWaiting = () => {
          // Waiting can happen during normal buffering, but repeated waiting
          // with no ready event is a strong signal to try blob fallback.
          console.warn(`${tag} media: waiting`);
        };

        media.addEventListener('loadstart', onMediaLoadStart);
        media.addEventListener('loadedmetadata', onMediaLoadedMetadata);
        media.addEventListener('canplay', onMediaCanPlay);
        media.addEventListener('error', onMediaError);
        media.addEventListener('stalled', onMediaStalled);
        media.addEventListener('waiting', onMediaWaiting);
      }

      console.log(`${tag} calling load() with URL: ${urlToLoad?.slice(0, 80)}...`);
      // Firebase Storage URLs of raw MediaRecorder WebM files (no duration
      // header) can freeze the renderer when loaded via <audio>.src streaming
      // — the media stack blocks the main thread before any of our timeouts
      // can run. Pre-fetching as a blob runs in the network stack, gives us
      // a known content-length, and bypasses the freeze entirely.
      const isFirebaseUrl = typeof urlToLoad === 'string' &&
        urlToLoad.includes('firebasestorage.googleapis.com');
      const usingBlobFirst = shouldPreferBlobLoad || isFirebaseUrl;
      if (shouldPreferBlobLoad) {
        console.log(`${tag} legacy session detected, using blob-first load`);
        attemptBlobFallback('legacy session');
      } else if (isFirebaseUrl) {
        console.log(`${tag} firebase URL — using blob-first load to avoid renderer freeze`);
        attemptBlobFallback('firebase blob-first');
      } else {
        wavesurferRef.current.load(urlToLoad).catch((err) => {
          if (!cancelled) {
            console.error(`${tag} load() rejected:`, err);
            attemptBlobFallback('load() rejection');
          }
        });
      }
      // For non-blob paths, give the streaming load 6s to reach ready before
      // falling back. Blob-first paths manage their own timeouts internally
      // (blobReadyTimeout in attemptBlobFallback) — running this outer one
      // would just race with that.
      if (!usingBlobFirst) {
        stalledLoadTimeout = setTimeout(() => {
          if (!cancelled && !didBecomeReady) {
            console.warn(`${tag} load timeout before ready`);
            attemptBlobFallback('ready timeout');
          }
        }, 6000);
      }
      // Absolute hard error: if nothing has reached ready by 18s — including
      // blob fallback and WebAudio retry — surface the error UI so the user
      // gets a Try Again button instead of an indefinite spinner.
      hardErrorTimeout = setTimeout(() => {
        if (!cancelled && !didBecomeReady) {
          console.error(`${tag} hard load timeout — giving up`);
          setAudioUrlError(true);
          setIsWaveformLoading(false);
        }
      }, 18000);

      // Region events
        if (regionsPluginRef.current) {
      if (!readOnly) {
        regionsPluginRef.current.on('region-created', (region) => {
          const newRegion = {
            id: region.id,
            start: region.start,
            end: region.end,
            label: region.content || `Region ${regions.length + 1}`,
            color: region.color,
            loop: false
          };
          setRegions(prev => [...prev, newRegion]);
          saveDataToFirebase([...regions, newRegion], comments, markers);
        });

        regionsPluginRef.current.on('region-updated', (region) => {
          const updatedRegions = regions.map(r => 
            r.id === region.id 
              ? { ...r, start: region.start, end: region.end, label: region.content }
              : r
          );
          setRegions(updatedRegions);
          saveDataToFirebase(updatedRegions, comments, markers);
        });
      }

      regionsPluginRef.current.on('region-clicked', (region, e) => {
        if (e.shiftKey) {
          setLoopRegion(region);
          setIsLooping(true);
          wavesurferRef.current.seekTo(region.start / duration);
          wavesurferRef.current.play();
        } else {
          wavesurferRef.current.seekTo(region.start / duration);
        }
      });
        }
      } catch (error) {
        console.error('Error initializing WaveSurfer:', error);
        if (!cancelled) { setAudioUrlError(true); setIsWaveformLoading(false); }
      }
    };

    initWaveSurfer();

    // Cleanup
    return () => {
      console.log(`${tag} cleanup — had wavesurfer=${!!wavesurferRef.current}`);
      cancelled = true;
      if (stalledLoadTimeout) {
        clearTimeout(stalledLoadTimeout);
        stalledLoadTimeout = null;
      }
      if (blobReadyTimeout) {
        clearTimeout(blobReadyTimeout);
        blobReadyTimeout = null;
      }
      if (hardErrorTimeout) {
        clearTimeout(hardErrorTimeout);
        hardErrorTimeout = null;
      }
      // Reset refreshing state so the component isn't stuck if the refresh
      // was in progress when this cleanup ran.
      setIsRefreshing(false);
      if (media) {
        if (onMediaLoadStart) media.removeEventListener('loadstart', onMediaLoadStart);
        if (onMediaLoadedMetadata) media.removeEventListener('loadedmetadata', onMediaLoadedMetadata);
        if (onMediaCanPlay) media.removeEventListener('canplay', onMediaCanPlay);
        if (onMediaError) media.removeEventListener('error', onMediaError);
        if (onMediaStalled) media.removeEventListener('stalled', onMediaStalled);
        if (onMediaWaiting) media.removeEventListener('waiting', onMediaWaiting);
      }
      if (wavesurferRef.current) {
        try {
        wavesurferRef.current.destroy();
        } catch (error) {
          console.warn('Error destroying WaveSurfer:', error);
        }
        wavesurferRef.current = null;
        regionsPluginRef.current = null;
      }
    };
  }, [audioUrl, refreshedUrl, userId, sessionId, recordingId, compact, readOnly]);

   // Save data to Firebase
   const saveDataToFirebase = useCallback(async (regionsData, commentsData, markersData) => {
    console.log('AudioPlayer: Attempting to save data to Firebase:', {
      recordingId,
      practiceItemId, 
      sessionId,
      regionsCount: regionsData.length,
      commentsCount: commentsData.length,
      markersCount: markersData.length,
      hasOnDataUpdate: !!onDataUpdate
    });
    
    if (!onDataUpdate) {
      console.warn('AudioPlayer: No onDataUpdate callback provided');
      return;
    }
    
    try {
      await onDataUpdate({
        recordingId,
        practiceItemId,
        sessionId,
        regions: regionsData,
        comments: commentsData,
        markers: markersData,
        lastUpdated: new Date()
      });
      console.log('AudioPlayer: Successfully called onDataUpdate');
    } catch (error) {
      console.error('Error saving audio data to Firebase:', error);
    }
  }, [recordingId, practiceItemId, sessionId, onDataUpdate]);

  const handlePlayPause = () => {
    if (wavesurferRef.current) {
      wavesurferRef.current.playPause();
    }
  };

  const handleSpeedChange = (newRate) => {
    setPlaybackRate(newRate);
    if (wavesurferRef.current) {
      wavesurferRef.current.setPlaybackRate(newRate);
    }
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    if (wavesurferRef.current) {
      wavesurferRef.current.setVolume(newVolume);
    }
  };

  const handleZoom = (direction) => {
    const newZoom = direction === 'in' 
      ? Math.min(zoom + 50, 500) 
      : Math.max(zoom - 50, 0);
    setZoom(newZoom);
    if (wavesurferRef.current) {
      wavesurferRef.current.zoom(newZoom);
    }
  };

  const skipTime = (seconds) => {
    if (wavesurferRef.current) {
      const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
      wavesurferRef.current.seekTo(newTime / duration);
    }
  };

  const addComment = (time, text) => {
    const newComment = {
      id: `comment_${Date.now()}`,
      time,
      text,
      timestamp: new Date()
    };
    const updatedComments = [...comments, newComment];
    setComments(updatedComments);
    saveDataToFirebase(regions, updatedComments, markers);
    setShowCommentModal(false);
  };

  const addMarker = (time, label) => {
    const newMarker = {
      id: `marker_${Date.now()}`,
      time,
      label,
      timestamp: new Date()
    };
    const updatedMarkers = [...markers, newMarker];
    setMarkers(updatedMarkers);
    saveDataToFirebase(regions, comments, updatedMarkers);
    setShowMarkerModal(false);
  };

  const deleteComment = (commentId) => {
    const updatedComments = comments.filter(c => c.id !== commentId);
    setComments(updatedComments);
    saveDataToFirebase(regions, updatedComments, markers);
  };

  const deleteMarker = (markerId) => {
    const updatedMarkers = markers.filter(m => m.id !== markerId);
    setMarkers(updatedMarkers);
    saveDataToFirebase(regions, comments, updatedMarkers);
  };

  const deleteRegion = (regionId) => {
    // Remove from WaveSurfer
    if (regionsPluginRef.current) {
      const wsRegions = regionsPluginRef.current.getRegions();
      const regionToDelete = wsRegions.find(r => r.id === regionId);
    if (regionToDelete) {
      regionToDelete.remove();
      }
    }
    
    // Remove from state
    const updatedRegions = regions.filter(r => r.id !== regionId);
    setRegions(updatedRegions);
    saveDataToFirebase(updatedRegions, comments, markers);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const seekToTime = (time) => {
    if (wavesurferRef.current) {
      wavesurferRef.current.seekTo(time / duration);
    }
  };

  // NEW: Download function
  const handleDownload = async () => {
    if (!audioUrl) return;
    
    try {
      // Create a link element and trigger download
      const link = document.createElement('a');
      link.href = audioUrl;
      
      // Generate a filename based on recording info
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `recording-${practiceItemId || 'unknown'}-${timestamp}.webm`;
      
      // For same-origin URLs, we can use the download attribute
      if (audioUrl.startsWith(window.location.origin) || audioUrl.startsWith('/') || audioUrl.startsWith('blob:')) {
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // For cross-origin URLs (like Firebase Storage), we need to fetch and create a blob
        const response = await fetch(audioUrl);
        if (!response.ok) throw new Error('Failed to fetch audio file');
        
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the blob URL
        URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      console.error('Error downloading audio:', error);
      alert('Failed to download audio file. You can try right-clicking the audio player and selecting "Save as..."');
    }
  };

  // Add retry function
  const handleRetry = useCallback(async () => {
    setAudioUrlError(false);
    setRefreshedUrl(null);
    setIsRefreshing(false);
  }, []);

  // IMPORTANT: We always render the waveform div so waveformRef is never
  // null — otherwise a cancelled refresh leaves isRefreshing=true and the
  // ref permanently unavailable, preventing any future load attempt.

  // Show error state with retry option
  if (audioUrlError && !isRefreshing) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg ${compact ? 'p-2' : 'p-4'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="text-red-600 text-sm">
              ⚠️ Recording temporarily unavailable
            </div>
          </div>
          <button
            onClick={handleRetry}
            className="text-sm bg-red-100 text-red-700 px-3 py-1 rounded hover:bg-red-200 transition-colors"
          >
            Try Again
          </button>
        </div>
        {!compact && (
          <div className="mt-2 text-xs text-red-500">
            This may be due to an expired access token. Click "Try Again" to refresh.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm border w-full ${compact ? 'p-2' : 'p-4'}`}>
      {/* Loading overlay — shown while refreshing URL */}
      {isRefreshing && (
        <div className={`bg-blue-50 border border-blue-200 rounded-lg ${compact ? 'p-2 mb-2' : 'p-4 mb-4'}`}>
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            <div className="text-blue-600 text-sm">
              Refreshing recording access...
            </div>
          </div>
        </div>
      )}
      {/* Waveform — always in the DOM so the ref stays valid */}
      <div className="relative">
        <div ref={waveformRef} className={compact ? "mb-2" : "mb-4"} style={isRefreshing ? { height: 0, overflow: 'hidden' } : undefined}></div>
        {isWaveformLoading && !isRefreshing && (
          <div className={`flex items-center justify-center ${compact ? 'py-4' : 'py-8'}`}>
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-200 border-t-blue-600"></div>
            <span className="ml-2 text-sm text-gray-500">Loading recording...</span>
          </div>
        )}
      </div>
      
      {/* Main Controls - Compact Layout */}
      {compact ? (
        // COMPACT VERSION
        <div className="space-y-2">
          {/* Essential controls in one row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <button
                onClick={handlePlayPause}
                className="p-1 bg-blue-500 text-white rounded-full hover:bg-blue-600"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
              </button>
              
              <button
                onClick={() => wavesurferRef.current?.seekTo(0)}
                className="p-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                <RotateCcw size={12} />
              </button>

              <button
                onClick={() => skipTime(-5)}
                className="p-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                <SkipBack size={12} />
              </button>

              <button
                onClick={() => skipTime(5)}
                className="p-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                <SkipForward size={12} />
              </button>
              
              <div className="text-xs text-gray-600">
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>
            </div>

            {/* Speed and volume in compact format */}
            <div className="flex items-center space-x-1">
              <select
                value={playbackRate}
                onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
                className="text-xs border rounded px-1 py-0.5 w-12"
              >
                <option value={0.5}>0.5x</option>
                <option value={0.75}>0.75x</option>
                <option value={1}>1x</option>
                <option value={1.25}>1.25x</option>
                <option value={1.5}>1.5x</option>
                <option value={2}>2x</option>
              </select>

              <div className="flex items-center">
                <Volume2 size={12} className="text-gray-600" />
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={volume}
                  onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                  className="w-12 ml-1"
                />
              </div>
            </div>
          </div>

          {/* Compact action buttons */}
          <div className="flex items-center justify-center space-x-1">
            {!readOnly && (
              <>
                <button
                  onClick={() => {
                    setCommentTime(currentTime);
                    setShowCommentModal(true);
                  }}
                  className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                >
                  💬
                </button>
                
                <button
                  onClick={() => {
                    setMarkerTime(currentTime);
                    setShowMarkerModal(true);
                  }}
                  className="px-2 py-1 bg-purple-500 text-white rounded text-xs hover:bg-purple-600"
                >
                  🔖
                </button>
              </>
            )}

            {/* Download button for compact mode */}
            <button
              onClick={handleDownload}
              className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
              title="Download audio file"
            >
              <Download size={10} />
            </button>

            {/* Zoom controls */}
            <button
              onClick={() => handleZoom('out')}
              className="p-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              <ZoomOut size={10} />
            </button>
            <button
              onClick={() => handleZoom('in')}
              className="p-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              <ZoomIn size={10} />
            </button>
          </div>
        </div>
      ) : (
        // FULL VERSION
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePlayPause}
              className="p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600"
            >
              {isPlaying ? <Pause size={20} /> : <Play size={20} />}
            </button>
            
            <button
              onClick={() => wavesurferRef.current?.seekTo(0)}
              className="p-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              <RotateCcw size={20} />
            </button>

            <button
              onClick={() => skipTime(-10)}
              className="p-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              <SkipBack size={16} />
            </button>

            <button
              onClick={() => skipTime(10)}
              className="p-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              <SkipForward size={16} />
            </button>
            
            <div className="text-sm text-gray-600">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>

          <div className="flex items-center flex-wrap gap-2">
            {/* Loop Toggle */}
            <button
              onClick={() => {
                setIsLooping(!isLooping);
                if (!isLooping) setLoopRegion(null);
              }}
              className={`p-1 rounded ${isLooping ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              <Repeat size={16} />
            </button>

            {/* Speed Control */}
            <select
              value={playbackRate}
              onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
              className="text-sm border rounded px-2 py-1"
            >
              <option value={0.25}>0.25x</option>
              <option value={0.5}>0.5x</option>
              <option value={0.75}>0.75x</option>
              <option value={1}>1x</option>
              <option value={1.25}>1.25x</option>
              <option value={1.5}>1.5x</option>
              <option value={2}>2x</option>
            </select>

            {/* Volume Control */}
            <div className="flex items-center space-x-1">
              <Volume2 size={16} className="text-gray-600" />
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                className="w-14"
              />
            </div>

            {/* Zoom Controls */}
            <button
              onClick={() => handleZoom('out')}
              className="p-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              <ZoomOut size={16} />
            </button>
            <button
              onClick={() => handleZoom('in')}
              className="p-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              <ZoomIn size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Action Buttons - Only show in full mode */}
      {!compact && (
        <div className="flex items-center justify-center flex-wrap gap-2 mb-4">
          {!readOnly && (
            <>
              <button
                onClick={() => {
                  setCommentTime(currentTime);
                  setShowCommentModal(true);
                }}
                className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
              >
                💬 Add Comment
              </button>
              
              <button
                onClick={() => {
                  setMarkerTime(currentTime);
                  setShowMarkerModal(true);
                }}
                className="px-3 py-1 bg-purple-500 text-white rounded text-sm hover:bg-purple-600"
              >
                🔖 Add Marker
              </button>
            </>
          )}

          {/* Download button for full mode */}
          <button
            onClick={handleDownload}
            className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
          >
            <Download size={14} className="mr-1" />
            Download
          </button>
        </div>
      )}

      {/* Lists - Show in compact form when compact=true */}
      {regions.length > 0 && (
        <div className={compact ? "mb-2" : "mb-4"}>
          <h4 className={`font-medium ${compact ? 'text-xs mb-1' : 'text-sm mb-2'}`}>Regions</h4>
          <div className={`space-y-1 overflow-y-auto ${compact ? 'max-h-16 text-xs' : 'max-h-32 text-sm'}`}>
            {regions.map(region => (
              <div key={region.id} className={`flex items-center justify-between rounded ${compact ? 'p-1 bg-gray-50' : 'p-2 bg-gray-50'}`}>
                <div className="flex items-center space-x-1">
                  <div 
                    className={`rounded ${compact ? 'w-2 h-2' : 'w-4 h-4'}`}
                    style={{ backgroundColor: region.color }}
                  ></div>
                  <span className="truncate">{region.label}</span>
                  <span className="text-gray-500">
                    {formatTime(region.start)} - {formatTime(region.end)}
                  </span>
                </div>
                {!compact && (
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => seekToTime(region.start)}
                      className="px-2 py-1 bg-blue-100 text-blue-600 rounded text-xs"
                    >
                      Go
                    </button>
                    <button
                      onClick={() => {
                        setLoopRegion(region);
                        setIsLooping(true);
                        seekToTime(region.start);
                      }}
                      className="px-2 py-1 bg-green-100 text-green-600 rounded text-xs"
                    >
                      Loop
                    </button>
                    {!readOnly && (
                      <button
                        onClick={() => deleteRegion(region.id)}
                        className="px-2 py-1 bg-red-100 text-red-600 rounded text-xs"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Markers */}
      {markers.length > 0 && (
        <div className={compact ? "mb-2" : "mb-4"}>
          <h4 className={`font-medium ${compact ? 'text-xs mb-1' : 'text-sm mb-2'}`}>Markers</h4>
          <div className={`space-y-1 overflow-y-auto ${compact ? 'max-h-16 text-xs' : 'max-h-32 text-sm'}`}>
            {markers.map(marker => (
              <div key={marker.id} className={`flex items-center justify-between rounded ${compact ? 'p-1 bg-purple-50' : 'p-2 bg-purple-50'}`}>
                <div className="flex items-center space-x-1">
                  <Bookmark size={compact ? 10 : 14} className="text-purple-500" />
                  <span className="truncate">{marker.label}</span>
                  <span className="text-gray-500">{formatTime(marker.time)}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => seekToTime(marker.time)}
                    className={`${compact ? 'px-1 py-0.5 text-xs' : 'px-2 py-1'} bg-purple-100 text-purple-600 rounded text-xs`}
                  >
                    Go
                  </button>
                  {!compact && !readOnly && (
                    <button
                      onClick={() => deleteMarker(marker.id)}
                      className="px-2 py-1 bg-red-100 text-red-600 rounded text-xs"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comments */}
      {comments.length > 0 && (
        <div className={compact ? "mb-2" : "mb-4"}>
          <h4 className={`font-medium ${compact ? 'text-xs mb-1' : 'text-sm mb-2'}`}>Comments</h4>
          <div className={`space-y-1 overflow-y-auto ${compact ? 'max-h-16 text-xs' : 'max-h-32 text-sm'}`}>
            {comments.map(comment => (
              <div key={comment.id} className={`flex items-start justify-between gap-2 rounded ${compact ? 'p-1 bg-blue-50' : 'p-2 bg-blue-50'}`}>
                <div className="flex items-start space-x-1 min-w-0">
                  <MessageSquare size={compact ? 10 : 14} className="text-blue-500 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium">{formatTime(comment.time)}</div>
                    <div className="text-gray-600 break-words">{comment.text}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => seekToTime(comment.time)}
                    className={`${compact ? 'px-1 py-0.5 text-xs' : 'px-2 py-1'} bg-blue-100 text-blue-600 rounded text-xs`}
                  >
                    Go
                  </button>
                  {!compact && !readOnly && (
                    <button
                      onClick={() => deleteComment(comment.id)}
                      className="px-2 py-1 bg-red-100 text-red-600 rounded text-xs"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comment Modal */}
      {showCommentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-medium mb-4">
              Add Comment at {formatTime(commentTime)}
            </h3>
            <textarea
              className="w-full border rounded p-2 mb-4"
              rows="3"
              placeholder="Enter your comment..."
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  addComment(commentTime, e.target.value);
                }
              }}
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowCommentModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={(e) => {
                  const textarea = e.target.closest('.bg-white').querySelector('textarea');
                  addComment(commentTime, textarea.value);
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Add Comment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Marker Modal */}
      {showMarkerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-medium mb-4">
              Add Marker at {formatTime(markerTime)}
            </h3>
            <input
              type="text"
              className="w-full border rounded p-2 mb-4"
              placeholder="Marker label..."
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  addMarker(markerTime, e.target.value);
                }
              }}
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowMarkerModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={(e) => {
                  const input = e.target.closest('.bg-white').querySelector('input');
                  addMarker(markerTime, input.value);
                }}
                className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
              >
                Add Marker
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioPlayer;